import type { PoolItem } from '.';
import type { PoolConstructor } from './Pool';
import { Pool } from './Pool';

export class PoolGroupClass {
  private readonly _poolsByClass: Map<PoolConstructor<unknown>, Pool> =
    new Map();

  /**
   * populates the pool with the specified number of instances
   * @param Class - Class type to prepopulate
   * @param total - number of instances to create
   */
  public prepopulate<T extends new (...args: any[]) => any>(
    Class: T,
    total: number,
  ): void {
    const classPool = this.getPool(Class);

    classPool.prepopulate(total);
  }

  /**
   * return an entity of the specified class type, initialised with the supplied data object.
   * Entity will be automatically returned to the pool when it is destroyed.
   * @param Class - the class of entity required
   * @param data - data object to initialise entity from
   */
  public get<T extends new (...args: any[]) => InstanceType<T>>(
    Class: T,
    data?: unknown,
  ): InstanceType<T> {
    const pool = this.getPool(Class);
    const item = pool.get() as PoolItem;

    item._pooled = true;
    item.init?.(data);

    return item as InstanceType<T>;
  }

  /**
   * puts an entity back into its pool. The entity will be reset and all signal listeners removed
   * @param entity - the entity to return to the pool
   */
  public return(item: PoolItem): void {
    item.reset?.();
    item._pooled = false;
    this._returnToPool(item);
  }

  /** returns a pool for a specific class */
  public getPool<T extends new (...args: any[]) => InstanceType<T>>(
    ClassType: T,
  ): Pool<any> {
    if (!this._poolsByClass.has(ClassType)) {
      this._poolsByClass.set(ClassType, new Pool(ClassType));
    }

    return this._poolsByClass.get(ClassType) as Pool<any>;
  }

  /** gets the usage stats of each pool in the system */
  public stats(): Record<string, { free: number; used: number; size: number }> {
    const stats = {} as Record<
      string,
      { free: number; used: number; size: number }
    >;

    this._poolsByClass.forEach((pool) => {
      // TODO: maybe we should allow the name to be set when `createEntity` is called
      const name = stats[pool['_classType'].name]
        ? pool['_classType'].name + (pool['_classType'] as any).ID
        : pool['_classType'].name;

      stats[name] = {
        free: pool.totalFree,
        used: pool.totalUsed,
        size: pool.totalSize,
      };
    });

    return stats;
  }

  /**
   * Return an object to the pool
   * @param object -
   */
  private _returnToPool(object: InstanceType<any>): void {
    this.getPool(object.constructor).return(object);
  }

  public clearAll() {
    this._poolsByClass.forEach((pool) => pool.clear());
    this._poolsByClass.clear();
  }
}

export const PoolGroup = new PoolGroupClass();
